/**
 * Índice principal do sistema Platform
 * Exporta todos os serviços e gerenciadores da plataforma via Service Factory
 */

// Factory principal
export { PlatformServiceFactory, platformServiceFactory } from './platform_service_factory';
export type { ServiceType, ServiceRegistry } from './platform_service_factory';

// Interfaces dos serviços
export type { BasePlatformService } from './interfaces/base_platform_service';
export type { IPlatformDetectionService } from './interfaces/platform_detection_service';
export type { IPermissionService } from './interfaces/permission_service';
export type { IStorageService } from './interfaces/storage_service';
export type { INetworkService } from './interfaces/network_service';
export type { IFeatureService } from './interfaces/feature_service';

// Types dos serviços (importados das interfaces)
export type { 
  PlatformInfo, 
  BrowserType, 
  PlatformType
} from './interfaces/platform_detection_service';

export type { 
  PermissionType, 
  PermissionStatus, 
  PermissionResult 
} from './interfaces/permission_service';

export type { 
  StorageType, 
  StorageOptions, 
  StorageInfo 
} from './interfaces/storage_service';

export type { 
  NetworkInfo, 
  ConnectionType, 
  EffectiveType, 
  NetworkEvent 
} from './interfaces/network_service';

export type { 
  FeatureReport 
} from './interfaces/feature_service';

// Importações para uso interno
import { platformServiceFactory } from './platform_service_factory';
import { StorageType } from './interfaces/storage_service';
import type { PlatformInfo } from './interfaces/platform_detection_service';
import type { NetworkInfo } from './interfaces/network_service';
import type { FeatureReport } from './interfaces/feature_service';

/**
 * Inicializa todos os serviços da plataforma usando Service Factory
 */
export class PlatformInitializer {
  private static isInitialized = false;

  static async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Inicializa todos os serviços via factory
      console.log('🏭 Inicializando Platform Service Factory...');
      await platformServiceFactory.initializeAll();

      // Obtém serviços através do factory
      const platformService = platformServiceFactory.getService('PlatformDetection');
      const networkService = platformServiceFactory.getService('Network');
      const storageService = platformServiceFactory.getService('Storage');
      const featureService = platformServiceFactory.getService('Feature');

      // Detecta a plataforma
      console.log('🔍 Detectando plataforma...');
      const platformInfo = await platformService.getPlatformInfo();
      console.log('📱 Plataforma detectada:', {
        browser: platformInfo.browser,
        type: platformInfo.type,
        version: platformInfo.version,
        isDesktop: platformInfo.isDesktop,
        isMobile: platformInfo.isMobile,
        isTablet: platformInfo.isTablet
      });

      // Gera relatório de features
      console.log('🚀 Verificando features disponíveis...');
      const featureReport = featureService.generateFeatureReport();
      console.log('✅ Features suportadas:', featureReport.supported.length);
      console.log('❌ Features não suportadas:', featureReport.unsupported.length);
      console.log('🔐 Features que precisam de permissão:', featureReport.needsPermission.length);

      // Verifica status da rede
      console.log('🌐 Verificando status da rede...');
      const networkInfo = networkService.getNetworkInfo();
      console.log('📶 Status da rede:', {
        isOnline: networkInfo.isOnline,
        type: networkInfo.type,
        effectiveType: networkInfo.effectiveType,
        downlink: `${networkInfo.downlink} Mbps`,
        rtt: `${networkInfo.rtt} ms`,
        saveData: networkInfo.saveData
      });

      // Verifica informações de armazenamento
      console.log('💾 Verificando storage disponível...');
      const storageTypes = [
        StorageType.LOCAL, 
        StorageType.SESSION, 
        StorageType.INDEXED_DB
      ] as const;
      
      for (const type of storageTypes) {
        const available = await storageService.isAvailable(type);
        if (available) {
          const info = await storageService.getStorageInfo(type);
          console.log(`📦 ${type}:`, {
            used: `${((info.used || 0) / 1024).toFixed(1)} KB`,
            total: (info.total === undefined || info.total === Infinity) ? '∞' : `${(info.total / 1024 / 1024).toFixed(1)} MB`,
            remaining: (info.remaining === undefined || info.remaining === Infinity) ? '∞' : `${(info.remaining / 1024 / 1024).toFixed(1)} MB`
          });
        } else {
          console.log(`❌ ${type}: não disponível`);
        }
      }

      this.isInitialized = true;
      console.log('🎉 Plataforma inicializada com sucesso!');

    } catch (error) {
      console.error('❌ Erro ao inicializar plataforma:', error);
      throw error;
    }
  }

  /**
   * Verifica se a plataforma foi inicializada
   */
  static get initialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Obtém sumário completo da plataforma
   */
  static async getPlatformSummary(): Promise<{
    platform: PlatformInfo;
    features: FeatureReport;
    network: NetworkInfo;
    capabilities: {
      canPushNotifications: boolean;
      canAccessCamera: boolean;
      canAccessMicrophone: boolean;
      canAccessLocation: boolean;
      canStoreOffline: boolean;
      canRunInBackground: boolean;
    };
  }> {
    const platformService = platformServiceFactory.getService('PlatformDetection');
    const featureService = platformServiceFactory.getService('Feature');
    const networkService = platformServiceFactory.getService('Network');
    const storageService = platformServiceFactory.getService('Storage');

    const platform = await platformService.getPlatformInfo();
    const features = featureService.generateFeatureReport();
    const network = networkService.getNetworkInfo();

    const capabilities = {
      canPushNotifications: featureService.isSupported('pushNotifications'),
      canAccessCamera: featureService.isSupported('mediaDevices'),
      canAccessMicrophone: featureService.isSupported('mediaDevices'),
      canAccessLocation: featureService.isSupported('geolocation'),
      canStoreOffline: await storageService.isAvailable(StorageType.INDEXED_DB),
      canRunInBackground: featureService.isSupported('serviceWorker')
    };

    return {
      platform,
      features,
      network,
      capabilities
    };
  }

  /**
   * Cleanup - limpa todos os recursos da plataforma
   */
  static cleanup(): void {
    platformServiceFactory.cleanup();
    this.isInitialized = false;
    console.log('🧹 Plataforma limpa');
  }
}

// Exportação default
export default PlatformInitializer;
